/**
 * Helpers for user-added, configurable indicator columns on the Stocks table.
 *
 * A `DynamicIndicatorCol` (defined in TopDownContext) references a shared-lib
 * indicator by id + a params override. Display (header label, ColKind, heat)
 * is DERIVED from the shared def here — so the persisted column stays minimal
 * and the math/definition is never duplicated.
 */
import {
  SHARED_INDICATOR_BY_ID,
  resolveParams,
  studyShortName,
  type SharedIndicatorDef,
} from "@/lib/indicators/shared";
import type { ColKind } from "./stockColumns";
import type { DynamicIndicatorCol } from "./TopDownContext";

/** Map the shared def's display hint onto the table's ColKind. */
export function colKindForDef(def: SharedIndicatorDef): ColKind {
  switch (def.format) {
    case "pct":
      return "pct";
    case "percent":
      return "pctplain";
    case "ratio":
      return "ratio";
    case "signed":
      return "force";
    case "agesign":
      return "agesign";
    case "state":
      return "state";
    case "quad":
      return "quad";
    case "age":
      return "age";
    case "int":
      return "int";
    case "compact":
      return "compact";
    case "num":
    default:
      return "num";
  }
}

/** Compact header names for indicators whose full label is too long for a
 *  column head. Falls back to `def.label`. Display-only — not a definition. */
const SHORT_LABEL: Record<string, string> = {
  fm: "fM",
  rvol: "RVol",
  "stoch-k": "Stoch %K",
  "macd-hist": "MACD hist",
  "hv-pct": "HV %",
  "vol-z": "Vol z",
  "bb-pctb": "BB %B",
  "bb-width": "BB width",
  "williams-r": "%R",
  "vs-sma": "%SMA",
  "vs-ema": "%EMA",
  "atr-pct": "ATR %",
};

/** Build a concise header label from the def + chosen params, e.g.
 *  "RSI 14", "%SMA 200", "MACD hist 12/26/9", "fM 13 (Price-only)". Number
 *  params named multiplier/mult are omitted (display noise); a non-default
 *  select (e.g. fM mode) appends its choice label. */
export function defaultDynamicLabel(
  def: SharedIndicatorDef,
  params: Record<string, number>,
): string {
  const base = studyShortName(SHORT_LABEL[def.id] ?? def.label);
  const parts: string[] = [];
  // Indicators with a small input set spell out every value (e.g. "RSI 14",
  // "MACD hist 12/26/9"). Many-input indicators (e.g. the Up/Down Dots union of
  // ~20 lane params) would produce an unreadable header, so they show ONLY the
  // values the user has changed from default — clean at default, explicit once
  // tuned. The header is renamable either way via the ⚙ Column title field.
  const numericCount = def.params.filter(
    (p) => p.kind === "number" && p.id !== "multiplier" && p.id !== "mult",
  ).length;
  const showAllNumbers = numericCount <= 5;
  for (const p of def.params) {
    const v = params[p.id] ?? p.default;
    if (p.kind === "number") {
      if (p.id === "multiplier" || p.id === "mult") continue;
      if (showAllNumbers || v !== p.default) parts.push(String(v));
    } else if (v !== p.default) {
      const choice = p.choices.find((c) => c.value === v);
      if (choice) parts.push(choice.label);
    }
  }
  return parts.length ? `${base} ${parts.join("/")}` : base;
}

/** True when every param equals its default — the case where a precomputed
 *  payload value (`def.panelKey`) is valid and a recompute can be skipped. */
export function isDefaultParams(
  def: SharedIndicatorDef,
  params: Record<string, number>,
): boolean {
  for (const p of def.params) {
    const v = params[p.id] ?? p.default;
    if (v !== p.default) return false;
  }
  return true;
}

/** Like isDefaultParams but ignores the listed param ids — used by the timeframe
 *  resolver, where only `tf` may vary and the column still reads a precomputed
 *  per-timeframe field. */
export function isDefaultExcept(
  def: SharedIndicatorDef,
  params: Record<string, number>,
  exceptIds: ReadonlyArray<string>,
): boolean {
  for (const p of def.params) {
    if (exceptIds.includes(p.id)) continue;
    const v = params[p.id] ?? p.default;
    if (v !== p.default) return false;
  }
  return true;
}

const bigKahuna = (v: number | null | undefined): number | null =>
  v == null ? null : v > 0.4 ? 1 : v < -0.4 ? -1 : 0;
const smallKahuna = (v: number | null | undefined): number | null =>
  v == null ? null : v >= 0.24 && v <= 0.4 ? 1 : v <= -0.24 && v >= -0.4 ? -1 : 0;

/** Frontend derivations of an indicator's DEFAULT-param value from precomputed
 *  payload fields — for signals whose state isn't a single field (e.g. Big /
 *  Small Kahuna = thresholded Δ%B). Keyed by indicator id; mirrors each def's
 *  default-param `computeSeries`. */
const PRECOMPUTED_DERIVE: Record<string, (row: Record<string, number | null>) => number | null> = {
  "mpp-big-kahuna": (r) => bigKahuna(r.kahuna_dpctb),
  "mpp-small-kahuna": (r) => smallKahuna(r.kahuna_dpctb),
  // Kahuna-Force 2 Days = KF dot same direction ≥ 2 sessions (default).
  "mpp-kf-2day": (r) =>
    r.kf_run == null ? null : r.kf_run >= 2 ? 1 : r.kf_run <= -2 ? -1 : 0,
  // RS / OBV — Age = |signed age column|, Trend = its sign. Both read the same
  // precomputed signed bars-since-cross field (rs_d_age / obv_d_age / weekly).
  "mpp-rsma-daily-age": (r) => (r.rs_d_age == null ? null : Math.abs(r.rs_d_age)),
  "mpp-rsma-daily": (r) => (r.rs_d_age == null ? null : Math.sign(r.rs_d_age)),
  "mpp-obv-daily-age": (r) => (r.obv_d_age == null ? null : Math.abs(r.obv_d_age)),
  "mpp-obv-daily-trend": (r) => (r.obv_d_age == null ? null : Math.sign(r.obv_d_age)),
  // HCD/LCD daily signal fires off the baked signed reversal column (hlcd_d):
  // +1 = HCD, -1 = LCD. Weekly/Monthly handled by the tf-resolver below.
  "mpp-hcd-daily": (r) => (r.hlcd_d == null ? null : r.hlcd_d === 1 ? 1 : 0),
  "mpp-lcd-daily": (r) => (r.hlcd_d == null ? null : r.hlcd_d === -1 ? 1 : 0),
};

/** Timeframe-consolidated indicators: read the precomputed column for the chosen
 *  `tf` so a NON-default timeframe still stays instant (no recompute). Used only
 *  when every OTHER param is default (the caller checks via isDefaultExcept). */
const TF_PRECOMPUTED: Record<string, (tf: number, row: Record<string, number | null>) => number | null> = {
  "mpp-bongo-signal": (tf, r) => (tf === 2 ? r.bongo_m_state : tf === 1 ? r.bongo_w_state : r.bongo_d_state) ?? null,
  "mpp-bongo-age": (tf, r) => (tf === 2 ? r.bongo_m_age : tf === 1 ? r.bongo_w_age : r.bongo_d_age) ?? null,
  // Force / Kahuna — Force from panelKey columns; Kahuna signals derive from the
  // (weekly) Δ%B column, Kahuna ages from panelKey columns.
  "mpp-force2": (tf, r) => (tf === 1 ? r.force2_w : r.force2) ?? null,
  "mpp-force2-age": (tf, r) => (tf === 1 ? r.force2_w_age : r.force2_age) ?? null,
  "mpp-force13": (tf, r) => (tf === 1 ? r.force13_w : r.force13) ?? null,
  "mpp-force13-age": (tf, r) => (tf === 1 ? r.force13_w_age : r.force13_age) ?? null,
  "mpp-big-kahuna": (tf, r) => bigKahuna(tf === 1 ? r.kahuna_dpctb_w : r.kahuna_dpctb),
  "mpp-big-kahuna-age": (tf, r) => (tf === 1 ? r.big_kahuna_w_age : r.big_kahuna_age) ?? null,
  "mpp-small-kahuna": (tf, r) => smallKahuna(tf === 1 ? r.kahuna_dpctb_w : r.kahuna_dpctb),
  "mpp-small-kahuna-age": (tf, r) => (tf === 1 ? r.small_kahuna_w_age : r.small_kahuna_age) ?? null,
  // RS — Trend/Age derive (sign/abs) from the signed rs_*_age column; Value/MA
  // read panelKey columns.
  "mpp-rsma-daily": (tf, r) => { const a = tf === 1 ? r.rs_w_age : r.rs_d_age; return a == null ? null : Math.sign(a); },
  "mpp-rsma-daily-age": (tf, r) => { const a = tf === 1 ? r.rs_w_age : r.rs_d_age; return a == null ? null : Math.abs(a); },
  "mpp-rs-daily-value": (tf, r) => (tf === 1 ? r.rs_w_value : r.rs_d_value) ?? null,
  "mpp-rs-daily-ma": (tf, r) => (tf === 1 ? r.rs_w_ma : r.rs_d_ma) ?? null,
  // OBV — Trend/Age derive (sign/abs) from the signed obv_*_age column.
  "mpp-obv-daily-trend": (tf, r) => { const a = tf === 1 ? r.obv_w_age : r.obv_d_age; return a == null ? null : Math.sign(a); },
  "mpp-obv-daily-age": (tf, r) => { const a = tf === 1 ? r.obv_w_age : r.obv_d_age; return a == null ? null : Math.abs(a); },
  // EMS — Trend/Cross/Age each read their daily/weekly panelKey column.
  "mpp-ems": (tf, r) => (tf === 1 ? r.ems_state_w : r.ems_state) ?? null,
  "mpp-ems-cross": (tf, r) => (tf === 1 ? r.ems_cross_w : r.ems_cross) ?? null,
  "mpp-ems-age": (tf, r) => (tf === 1 ? r.ems_w_age : r.ems_age) ?? null,
  // CPR — Trend (regime), Signal (flip dot), Age each read a daily/weekly column.
  "mpp-cpr": (tf, r) => (tf === 1 ? r.cpr_state_w : r.cpr_state_d) ?? null,
  "mpp-cpr-daily": (tf, r) => (tf === 1 ? r.cpr_signal_w : r.cpr_signal_d) ?? null,
  "mpp-cpr-age": (tf, r) => (tf === 1 ? r.cpr_w_age : r.cpr_age) ?? null,
  // HCD/LCD signal — derive the fire from the daily/weekly/monthly signed
  // reversal column (hlcd_d / rev_w / hlcd_m). Age reads its baked per-timeframe
  // column (Daily via panelKey; Weekly/Monthly here). tf: 0=Daily 1=Weekly 2=Monthly.
  "mpp-hcd-daily": (tf, r) => { const c = tf === 2 ? r.hlcd_m : tf === 1 ? r.rev_w : r.hlcd_d; return c == null ? null : c === 1 ? 1 : 0; },
  "mpp-lcd-daily": (tf, r) => { const c = tf === 2 ? r.hlcd_m : tf === 1 ? r.rev_w : r.hlcd_d; return c == null ? null : c === -1 ? 1 : 0; },
  "mpp-hcd-daily-age": (tf, r) => (tf === 2 ? r.hcd_m_age : tf === 1 ? r.hcd_w_age : r.hcd_d_age) ?? null,
  "mpp-lcd-daily-age": (tf, r) => (tf === 2 ? r.lcd_m_age : tf === 1 ? r.lcd_w_age : r.lcd_d_age) ?? null,
  // JP Signal / Trend (PPS) — served fields: jp_signal_* → signal_*, jp_trend_*
  // → cpr_d / cpr_w / trend_m (historical aliases). tf: 0=Daily 1=Weekly 2=Monthly.
  "mpp-jp-signal": (tf, r) => (tf === 2 ? r.signal_m : tf === 1 ? r.signal_w : r.signal_d) ?? null,
  "mpp-jp-trend": (tf, r) => (tf === 2 ? r.trend_m : tf === 1 ? r.cpr_w : r.cpr_d) ?? null,
  // Volume Histogram (Persons VM) — baked daily/weekly/monthly columns.
  "jp-vm": (tf, r) => (tf === 2 ? r.vol_hist_m : tf === 1 ? r.vol_hist_w : r.vol_hist_d) ?? null,
  // JP Histogram (pivot-momentum) — baked daily/weekly/monthly columns.
  "mpp-jp-histogram": (tf, r) => (tf === 2 ? r.hist_m : tf === 1 ? r.hist_w : r.hist_d) ?? null,
  // LCC Trend + floor pivots — baked daily/weekly/monthly columns.
  "mpp-lcc-trend": (tf, r) => (tf === 2 ? r.lcc_m : tf === 1 ? r.lcc_w : r.lcc_d) ?? null,
  "mpp-jp-pivot": (tf, r) => (tf === 2 ? r.pivot_m : tf === 1 ? r.pivot_w : r.pivot_d) ?? null,
  "mpp-jp-pivot-ma": (tf, r) => (tf === 2 ? r.pivotma_m : tf === 1 ? r.pivotma_w : r.pivotma_d) ?? null,
  "mpp-jp-support": (tf, r) => (tf === 2 ? r.support_m : tf === 1 ? r.support_w : r.support_d) ?? null,
  "mpp-jp-resistance": (tf, r) => (tf === 2 ? r.resist_m : tf === 1 ? r.resist_w : r.resist_d) ?? null,
  "mpp-jp-dist-support": (tf, r) => (tf === 2 ? r.distsup_m : tf === 1 ? r.distsup_w : r.distsup_d) ?? null,
  "mpp-jp-dist-resistance": (tf, r) => (tf === 2 ? r.distres_m : tf === 1 ? r.distres_w : r.distres_d) ?? null,
  // OC Rule / OC S/R / Pivot Status — baked daily/weekly/monthly columns.
  "mpp-jp-oc-rule": (tf, r) => (tf === 2 ? r.oc_rule_m : tf === 1 ? r.oc_rule_w : r.oc_rule_d) ?? null,
  "mpp-jp-oc-sr": (tf, r) => (tf === 2 ? r.oc_sr_m : tf === 1 ? r.oc_sr_w : r.oc_sr_d) ?? null,
  "mpp-jp-pivot-status": (tf, r) => (tf === 2 ? r.pstatus_m : tf === 1 ? r.pstatus_w : r.pstatus_d) ?? null,
};

/** Legacy per-timeframe indicator ids → the consolidated id + the `tf` value that
 *  reproduces them, so a user's saved columns survive the consolidation. Applied
 *  when persisted dynamic columns are loaded (see TopDownContext). */
export const LEGACY_INDICATOR_ALIAS: Record<string, { id: string; tf: number }> = {
  "mpp-bongo-daily": { id: "mpp-bongo-signal", tf: 0 },
  "mpp-bongo-weekly": { id: "mpp-bongo-signal", tf: 1 },
  "mpp-bongo-monthly": { id: "mpp-bongo-signal", tf: 2 },
  "mpp-bongo-daily-age": { id: "mpp-bongo-age", tf: 0 },
  "mpp-bongo-weekly-age": { id: "mpp-bongo-age", tf: 1 },
  "mpp-bongo-monthly-age": { id: "mpp-bongo-age", tf: 2 },
  // Force / Kahuna — daily ids are reused as canonical, only the weekly ids alias.
  "mpp-force2-w": { id: "mpp-force2", tf: 1 },
  "mpp-force2-w-age": { id: "mpp-force2-age", tf: 1 },
  "mpp-force13-w": { id: "mpp-force13", tf: 1 },
  "mpp-force13-w-age": { id: "mpp-force13-age", tf: 1 },
  "mpp-big-kahuna-w": { id: "mpp-big-kahuna", tf: 1 },
  "mpp-big-kahuna-w-age": { id: "mpp-big-kahuna-age", tf: 1 },
  "mpp-small-kahuna-w": { id: "mpp-small-kahuna", tf: 1 },
  "mpp-small-kahuna-w-age": { id: "mpp-small-kahuna-age", tf: 1 },
  // RS / OBV — daily ids reused as canonical, only the weekly ids alias.
  "mpp-rsma-weekly": { id: "mpp-rsma-daily", tf: 1 },
  "mpp-rsma-weekly-age": { id: "mpp-rsma-daily-age", tf: 1 },
  "mpp-rs-weekly-value": { id: "mpp-rs-daily-value", tf: 1 },
  "mpp-rs-weekly-ma": { id: "mpp-rs-daily-ma", tf: 1 },
  "mpp-obv-weekly-trend": { id: "mpp-obv-daily-trend", tf: 1 },
  "mpp-obv-weekly-age": { id: "mpp-obv-daily-age", tf: 1 },
  // PMC — no baked columns (recomputes via API for both tf); only weekly aliases.
  "mpp-pmc-weekly": { id: "mpp-pmc-daily", tf: 1 },
  "mpp-pmc-weekly-age": { id: "mpp-pmc-daily-age", tf: 1 },
  "mpp-pmc-weekly-value": { id: "mpp-pmc-daily-value", tf: 1 },
  "mpp-pmc-weekly-ma": { id: "mpp-pmc-daily-ma", tf: 1 },
  // EMS — daily ids (mpp-ems / -cross / -age) reused as canonical; weekly aliases.
  "mpp-ems-weekly-trend": { id: "mpp-ems", tf: 1 },
  "mpp-ems-weekly-cross": { id: "mpp-ems-cross", tf: 1 },
  "mpp-ems-weekly-age": { id: "mpp-ems-age", tf: 1 },
  // CPR — mpp-cpr (Trend), mpp-cpr-daily (Signal), mpp-cpr-age reused as canonical.
  "mpp-cpr-weekly": { id: "mpp-cpr", tf: 1 },
  "mpp-cpr-weekly-signal": { id: "mpp-cpr-daily", tf: 1 },
  "mpp-cpr-weekly-age": { id: "mpp-cpr-age", tf: 1 },
  // HCD/LCD — Daily/Weekly/Monthly (tf 0/1/2). Daily ids reused as canonical.
  "mpp-hcd-weekly": { id: "mpp-hcd-daily", tf: 1 },
  "mpp-hcd-monthly": { id: "mpp-hcd-daily", tf: 2 },
  "mpp-lcd-weekly": { id: "mpp-lcd-daily", tf: 1 },
  "mpp-lcd-monthly": { id: "mpp-lcd-daily", tf: 2 },
  "mpp-hcd-weekly-age": { id: "mpp-hcd-daily-age", tf: 1 },
  "mpp-hcd-monthly-age": { id: "mpp-hcd-daily-age", tf: 2 },
  "mpp-lcd-weekly-age": { id: "mpp-lcd-daily-age", tf: 1 },
  "mpp-lcd-monthly-age": { id: "mpp-lcd-daily-age", tf: 2 },
};

/** Rewrite a persisted column whose per-timeframe indicator id was consolidated
 *  into a single def + `tf` param. Also migrates the legacy `cue-*` id prefix to
 *  `mpp-*` (the CUE → MPP rename) so saved columns survive. No-op for current ids. */
export function migrateLegacyCol(col: DynamicIndicatorCol): DynamicIndicatorCol {
  // CUE → MPP: every old `cue-<x>` id is now `mpp-<x>`.
  let c = col;
  if (c.indicatorId.startsWith("cue-")) {
    c = { ...c, indicatorId: `mpp-${c.indicatorId.slice(4)}` };
  }
  const a = LEGACY_INDICATOR_ALIAS[c.indicatorId];
  return a ? { ...c, indicatorId: a.id, params: { ...c.params, tf: a.tf } } : c;
}

/** True when a column reads a precomputed value (no `/api/scanner/indicator`
 *  fetch) — params are default AND the def has a `panelKey` or a derive, OR only
 *  the `tf` differs and the def has a timeframe resolver. */
export function isPrecomputedDefault(col: DynamicIndicatorCol): boolean {
  const def = SHARED_INDICATOR_BY_ID.get(col.indicatorId);
  if (!def) return false;
  if (isDefaultParams(def, col.params)) {
    return !!def.panelKey || col.indicatorId in PRECOMPUTED_DERIVE;
  }
  return col.indicatorId in TF_PRECOMPUTED && isDefaultExcept(def, col.params, ["tf"]);
}

/** The column's precomputed value read from the payload row, or `undefined` when
 *  it must be recomputed (a non-tf param changed / no precompute). */
export function precomputedDefaultValue(
  col: DynamicIndicatorCol,
  row: Record<string, number | null>,
): number | null | undefined {
  const def = SHARED_INDICATOR_BY_ID.get(col.indicatorId);
  if (!def) return undefined;
  if (isDefaultParams(def, col.params)) {
    if (def.panelKey) return row[def.panelKey] ?? null;
    const derive = PRECOMPUTED_DERIVE[col.indicatorId];
    return derive ? derive(row) : undefined;
  }
  if (!isDefaultExcept(def, col.params, ["tf"])) return undefined;
  const tf = TF_PRECOMPUTED[col.indicatorId];
  return tf ? tf(col.params.tf ?? 0, row) : undefined;
}

const newColId = () =>
  `dyn-${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-4)}`;

/** Build a fresh dynamic column for an indicator id at the given (or default)
 *  params, with an auto-generated label. Returns null for an unknown id. */
export function makeDynamicCol(
  indicatorId: string,
  paramOverrides?: Record<string, number>,
): DynamicIndicatorCol | null {
  const def = SHARED_INDICATOR_BY_ID.get(indicatorId);
  if (!def) return null;
  const params = resolveParams(def, paramOverrides);
  return {
    colId: newColId(),
    indicatorId,
    params,
    label: defaultDynamicLabel(def, params),
  };
}
